import { useState, useEffect } from 'react';
import { GovernanceColors } from '@/config/governanceColors';
import { Monitor, Wifi, WifiOff, AlertTriangle, Zap, RefreshCw, Eye } from 'lucide-react';

interface DesktopEndpoint {
  id: string;
  hostname: string;
  os: string;
  daemonVersion: string;
  governedAgents: number;
  ungovernedAgents: number;
  lastHeartbeat: number;
  online: boolean;
  lastPolicySync: number;
}

interface ShadowAlert {
  id: string;
  endpointId: string;
  hostname: string;
  agentName: string;
  detectedAt: number;
  severity: 'critical' | 'warning' | 'info';
  description: string;
}

async function fetchEndpoints(): Promise<DesktopEndpoint[]> {
  const res = await fetch('/api/desktop/endpoints');
  if (!res.ok) throw new Error('Failed to fetch endpoints');
  return res.json();
}

async function fetchAlerts(): Promise<ShadowAlert[]> {
  const res = await fetch('/api/desktop/alerts');
  if (!res.ok) throw new Error('Failed to fetch alerts');
  return res.json();
}

async function haltEndpoint(id: string): Promise<void> {
  const res = await fetch(`/api/desktop/endpoints/${id}/halt`, { method: 'POST' });
  if (!res.ok) throw new Error('Halt failed');
}

async function haltAll(): Promise<void> {
  const res = await fetch('/api/desktop/halt-all', { method: 'POST' });
  if (!res.ok) throw new Error('Fleet halt failed');
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

export function DesktopDaemonView() {
  const [endpoints, setEndpoints] = useState<DesktopEndpoint[]>([]);
  const [alerts, setAlerts] = useState<ShadowAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [haltingId, setHaltingId] = useState<string | null>(null);
  const [haltingAll, setHaltingAll] = useState(false);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [eps, alts] = await Promise.all([fetchEndpoints(), fetchAlerts()]);
      setEndpoints(eps);
      setAlerts(alts);
    } catch (e: any) {
      setError(e.message || 'Failed to load desktop data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function handleHalt(id: string) {
    setHaltingId(id);
    try {
      await haltEndpoint(id);
      await refresh();
    } catch {
      setError('Halt command failed');
    } finally {
      setHaltingId(null);
    }
  }

  async function handleHaltAll() {
    setHaltingAll(true);
    try {
      await haltAll();
      await refresh();
    } catch {
      setError('Fleet halt failed');
    } finally {
      setHaltingAll(false);
    }
  }

  const onlineCount = endpoints.filter(e => e.online).length;
  const totalUngoverned = endpoints.reduce((sum, e) => sum + e.ungovernedAgents, 0);

  if (loading) {
    return <div className="text-[#A8A29E] text-sm">Loading desktop endpoints...</div>;
  }

  if (error && endpoints.length === 0) {
    return (
      <div className="text-[#A8A29E] text-sm p-4">
        <p className="font-medium text-[#C9D1D9] mb-2">Desktop daemon governance</p>
        <p>Desktop daemon governance monitors agent actions on developer workstations. Install the desktop daemon to enable.</p>
        <p className="mt-2 text-xs">Run <code style={{ color: GovernanceColors.primaryLight }}>npx @agentomy/desktop-daemon install</code> on target workstations.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="desktop-daemon-view">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Monitor className="w-5 h-5" style={{ color: GovernanceColors.primary }} />
          Desktop Daemon Fleet
        </h2>
        <div className="flex items-center gap-3">
          <button
            onClick={refresh}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm bg-[#292524] hover:bg-[#3D3833] text-[#A8A29E] hover:text-[#F5F0E8] border border-[#3A352F] transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            onClick={handleHaltAll}
            disabled={haltingAll || endpoints.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-sm bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-600/40 transition-colors disabled:opacity-50"
            data-testid="halt-all-btn"
          >
            <Zap className="w-3.5 h-3.5" /> {haltingAll ? 'Halting...' : 'Halt All Endpoints'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded px-4 py-2 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-[#292524] rounded-lg px-4 py-3 border border-[#2E2A26]">
          <p className="text-xs text-[#A8A29E]">Connected</p>
          <p className="text-2xl font-bold text-[#F5F0E8]">{onlineCount}</p>
        </div>
        <div className="bg-[#292524] rounded-lg px-4 py-3 border border-[#2E2A26]">
          <p className="text-xs text-[#A8A29E]">Total Endpoints</p>
          <p className="text-2xl font-bold text-[#F5F0E8]">{endpoints.length}</p>
        </div>
        <div className="bg-[#292524] rounded-lg px-4 py-3 border border-[#2E2A26]">
          <p className="text-xs text-[#A8A29E]">Ungoverned Agents</p>
          <p
            className={`text-2xl font-bold ${totalUngoverned > 0 ? 'text-amber-400' : ''}`}
            style={totalUngoverned > 0 ? undefined : { color: GovernanceColors.healthy }}
          >
            {totalUngoverned}
          </p>
        </div>
        <div className="bg-[#292524] rounded-lg px-4 py-3 border border-[#2E2A26]">
          <p className="text-xs text-[#A8A29E]">Shadow Alerts</p>
          <p
            className={`text-2xl font-bold ${alerts.length > 0 ? 'text-red-400' : ''}`}
            style={alerts.length > 0 ? undefined : { color: GovernanceColors.healthy }}
          >
            {alerts.length}
          </p>
        </div>
      </div>

      {/* Endpoint table */}
      <div className="bg-[#1C1917] border border-[#2E2A26] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="endpoint-table">
          <thead>
            <tr className="border-b border-[#2E2A26] text-[#A8A29E] text-xs uppercase">
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Hostname</th>
              <th className="text-left px-4 py-3">OS</th>
              <th className="text-left px-4 py-3">Version</th>
              <th className="text-right px-4 py-3">Governed</th>
              <th className="text-right px-4 py-3">Ungoverned</th>
              <th className="text-left px-4 py-3">Last Heartbeat</th>
              <th className="text-left px-4 py-3">Policy Sync</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {endpoints.map(ep => (
              <tr key={ep.id} className="border-b border-[#2E2A26]/50 hover:bg-[#292524]">
                <td className="px-4 py-3">
                  {ep.online ? (
                    <Wifi className="w-4 h-4" style={{ color: GovernanceColors.healthy }} />
                  ) : (
                    <WifiOff className="w-4 h-4" style={{ color: GovernanceColors.critical }} />
                  )}
                </td>
                <td className="px-4 py-3 text-[#F5F0E8] font-mono text-xs">{ep.hostname}</td>
                <td className="px-4 py-3 text-[#A8A29E]">{ep.os}</td>
                <td className="px-4 py-3 text-[#A8A29E] font-mono text-xs">{ep.daemonVersion}</td>
                <td className="px-4 py-3 text-right" style={{ color: GovernanceColors.healthy }}>{ep.governedAgents}</td>
                <td className={`px-4 py-3 text-right ${ep.ungovernedAgents > 0 ? 'text-amber-400' : 'text-[#A8A29E]'}`}>
                  {ep.ungovernedAgents}
                </td>
                <td className="px-4 py-3 text-xs text-[#A8A29E]">{timeAgo(ep.lastHeartbeat)}</td>
                <td className="px-4 py-3 text-xs text-[#A8A29E]">{timeAgo(ep.lastPolicySync)}</td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => handleHalt(ep.id)}
                    disabled={haltingId === ep.id}
                    className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                    data-testid={`halt-${ep.id}`}
                  >
                    {haltingId === ep.id ? 'Halting...' : 'Halt'}
                  </button>
                </td>
              </tr>
            ))}
            {endpoints.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-[#A8A29E]">
                  No desktop endpoints connected
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Shadow AI Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold flex items-center gap-2 text-amber-400">
            <Eye className="w-4 h-4" /> Shadow AI Alerts
          </h3>
          <div className="space-y-2">
            {alerts.map(alert => (
              <div
                key={alert.id}
                className="flex items-start gap-3 px-4 py-3 bg-[#292524] border border-[#2E2A26] rounded-lg"
              >
                <AlertTriangle className={`w-4 h-4 mt-0.5 ${alert.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#F5F0E8]">{alert.agentName}</span>
                    <span className="text-xs text-[#A8A29E]">on {alert.hostname}</span>
                  </div>
                  <p className="text-xs text-[#A8A29E] mt-0.5">{alert.description}</p>
                  <span className="text-[10px] text-[#8F857B]">{timeAgo(alert.detectedAt)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
